// src/pages/AudioSentiment.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import WaveSurfer from "wavesurfer.js";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

// ---- API (hard-coded) ----
const API_ROOT = "http://localhost:5000/api"; // ⬅️ uses :5000

// Chart slice colors (positive, neutral, negative)
const COLORS = ["#22c55e", "#f59e0b", "#ef4444"];

export default function AudioSentiment() {
  const fileInputRef = useRef(null);
  const waveRef = useRef(null);     // DOM container for WaveSurfer
  const wsRef = useRef(null);       // WaveSurfer instance

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);     // overall summary from API (all speakers)
  const [rows, setRows] = useState([]);             // sentiment rows (may include Speaker/Start/End)
  const [error, setError] = useState("");
  const [diarization, setDiarization] = useState([]); // raw diarized utterances from API
  const [speakerFilter, setSpeakerFilter] = useState("ALL"); // "ALL" | "S0" | "S1" | ...

  // Init WaveSurfer once
  useEffect(() => {
    if (!waveRef.current) return;
    wsRef.current = WaveSurfer.create({
      container: waveRef.current,
      height: 110,
      waveColor: "#6b7280",
      progressColor: "#e5e7eb",
      cursorColor: "#111827",
      normalize: true,
    });
    return () => {
      try { wsRef.current?.destroy(); } catch {}
    };
  }, []);

  const onFileChange = (e) => {
    setError("");
    const f = e.target.files?.[0] || null;
    setFile(f);
    setFileName(f ? f.name : "");
    if (f) {
      const url = URL.createObjectURL(f);
      wsRef.current?.load(url);
      wsRef.current?.once("ready", () => URL.revokeObjectURL(url));
    } else {
      wsRef.current?.empty();
    }
  };

  const analyze = async () => {
    if (!file) {
      setError("Choose an audio file first.");
      return;
    }
    try {
      setBusy(true);
      setError("");
      setRows([]);
      setSummary(null);
      setDiarization([]);
      setSpeakerFilter("ALL");

      const form = new FormData();
      form.append("audio", file);

      // Your backend now uses AssemblyAI and returns:
      // { transcript, summary, rows, diarization, provider }
      const { data } = await axios.post(
        `${API_ROOT}/audio-sentiment/analyze`,
        form,
        { timeout: 300000 } // up to 5 minutes
      );

      setSummary(data.summary || null);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setDiarization(Array.isArray(data.diarization) ? data.diarization : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || "Upload/analysis failed.");
    } finally {
      setBusy(false);
    }
  };

  // Speakers present (from diarization or from row-level Speaker)
  const speakers = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.Speaker) set.add(r.Speaker); });
    diarization.forEach(u => { if (u.speaker) set.add(u.speaker); });
    return ["ALL", ...Array.from(set).sort()];
  }, [rows, diarization]);

  // Filtered rows by speaker (if a specific speaker is selected)
  const filteredRows = useMemo(() => {
    if (speakerFilter === "ALL") return rows;
    return rows.filter(r => r.Speaker === speakerFilter);
  }, [rows, speakerFilter]);

  // Recompute donut values based on the filtered rows
  const donutSummary = useMemo(() => {
    const base = { positive: 0, neutral: 0, negative: 0 };
    for (const r of filteredRows) {
      if (r.Polarity === "P") base.positive++;
      else if (r.Polarity === "N") base.negative++;
      else base.neutral++;
    }
    return base;
  }, [filteredRows]);

  const donutData = [
    { name: "Positive", value: donutSummary.positive },
    { name: "Neutral", value: donutSummary.neutral },
    { name: "Negative", value: donutSummary.negative },
  ];

  const total = donutSummary.positive + donutSummary.neutral + donutSummary.negative;

  const center = (() => {
    if (!total) return { label: "—", val: 0 };
    const arr = [
      ["Positive", donutSummary.positive],
      ["Neutral", donutSummary.neutral],
      ["Negative", donutSummary.negative],
    ].sort((a, b) => b[1] - a[1]);
    return { label: arr[0][0], val: arr[0][1] };
  })();

  // small helper: format seconds to mm:ss
  const fmt = (s) => {
    if (s == null || Number.isNaN(s)) return "";
    const sec = Math.max(0, Math.floor(s));
    const m = Math.floor(sec / 60);
    const r = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };

  return (
    // Make the page root relative so the overlay is confined here (NOT full screen)
    <div className="relative w-full min-h-[60vh] flex flex-col p-3 md:p-6 lg:p-8">
      {/* Page Title */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
          Audio Sentiment Analysis
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Upload an audio file to transcribe with diarization and generate row-level sentiment plus a summary chart.
        </p>
      </div>

      {/* Uploader Card */}
      <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md">
        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b flex items-center justify-between">
          <h3 className="text-sm font-bold">Audio Processing</h3>
        </div>

        <div className="p-3">
          <div className="mb-3">
            <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
              Audio File
            </label>
            <div className="flex items-center flex-wrap gap-2">
              <div className="relative flex-grow min-w-[260px]">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="audio/*"
                  className="hidden"
                  onChange={onFileChange}
                  disabled={busy}
                />
                <div className="flex items-center">
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 text-xs rounded-lg w-full p-2 flex items-center">
                    <span className="truncate">{fileName || "No file selected"}</span>
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                    className="ml-2 px-3 py-2 text-xs font-medium text-white dark:text-black bg-gray-700 dark:bg-uasYellow rounded-lg hover:bg-gray-800 dark:hover:bg-yellow-500 disabled:opacity-60 focus:outline-none"
                  >
                    Browse
                  </button>
                </div>
              </div>

              {/* Speaker filter (only shown if diarization present) */}
              {speakers.length > 1 && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-700 dark:text-gray-300">Speaker</label>
                  <select
                    className="text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1"
                    value={speakerFilter}
                    onChange={(e) => setSpeakerFilter(e.target.value)}
                    disabled={busy}
                  >
                    {speakers.map(spk => (
                      <option key={spk} value={spk}>{spk}</option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={analyze}
                disabled={busy || !file}
                aria-busy={busy}
                className="relative inline-flex items-center justify-center overflow-hidden p-0.5 text-xs font-medium rounded-lg group bg-gradient-to-br from-black to-gray-800 disabled:opacity-60 dark:from-uasYellow dark:to-yellow-500"
              >
                <span className="relative w-full px-3 py-2 transition-all ease-in duration-75 bg-black text-white dark:bg-transparent dark:text-black rounded-md group-hover:bg-opacity-0 group-hover:text-white dark:group-hover:text-black flex items-center justify-center gap-2">
                  {busy && <Spinner size={16} light />}
                  {busy ? "Analyzing..." : "Upload & Analyze"}
                </span>
              </button>
            </div>
          </div>
          {error && (
            <div className="text-xs text-red-500 dark:text-red-300">{error}</div>
          )}
        </div>
      </div>

      {/* Main Grid (TOP: chart + spectrogram, BOTTOM: full-width table) */}
      <div className="grid gap-3 mt-4">
        {/* TOP ROW */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Donut Card */}
          <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md overflow-hidden">
            <div className="px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b">
              <h3 className="text-sm font-bold">
                Sentiment Analysis chart {speakerFilter !== "ALL" ? `— ${speakerFilter}` : "(in %)"}
              </h3>
            </div>
            <div className="p-3">
              <div className="h-64 relative">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={donutData}
                      innerRadius={"55%"}
                      outerRadius={"85%"}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {donutData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="text-base font-bold text-gray-900 dark:text-white">
                      {center.label}
                    </div>
                    <div className="text-gray-500 dark:text-gray-300">
                      {total ? Math.round((center.val / total) * 100) : 0}%
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-3 text-sm">
                <Legend color="#22c55e" label="Positive" />
                <Legend color="#f59e0b" label="Neutral" />
                <Legend color="#ef4444" label="Negative" />
              </div>
            </div>
          </div>

          {/* Spectrogram Card */}
          <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md overflow-hidden">
            <div className="px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b">
              <h3 className="text-sm font-bold">Audio Spectrogram</h3>
            </div>
            <div className="p-3">
              <div
                ref={waveRef}
                className="rounded-lg"
                style={{ background: "#0b1220", height: 120 }}
                aria-label="Waveform preview"
              />
              <div className="mt-3 flex gap-2">
                <Btn onClick={() => wsRef.current?.playPause()} disabled={!file || busy}>
                  ▶ / ⏸
                </Btn>
                <Btn onClick={() => wsRef.current?.stop()} disabled={!file || busy}>
                  ⏹
                </Btn>
                <Btn
                  onClick={() => wsRef.current?.setMuted(!wsRef.current?.getMuted())}
                  disabled={!file || busy}
                >
                  🔈
                </Btn>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM ROW: Full-width Table */}
        <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md overflow-hidden">
          <div className="px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b">
            <h3 className="text-sm font-bold">Sentiment Analysis</h3>
          </div>

          <div className="p-3">
            <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="min-w-full text-sm text-gray-900 dark:text-gray-100">
                <thead className="bg-[#111111] text-white sticky top-0">
                  <tr>
                    <Th>Speaker</Th>
                    <Th>Start</Th>
                    <Th>End</Th>
                    <Th>Text</Th>
                    <Th>Polarity</Th>
                    <Th>Agreement</Th>
                    <Th>Subjectivity</Th>
                    <Th>Confidence</Th>
                    <Th>Irony</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, i) => (
                    <tr
                      key={i}
                      className={i % 2 ? "bg-gray-50 dark:bg-gray-800/40" : ""}
                    >
                      <Td className="whitespace-nowrap">{r.Speaker || ""}</Td>
                      <Td className="whitespace-nowrap">{fmt(r.Start)}</Td>
                      <Td className="whitespace-nowrap">{fmt(r.End)}</Td>
                      <Td className="max-w-[800px]">{r.Text}</Td>
                      <Td>{r.Polarity}</Td>
                      <Td>{r.Agreement}</Td>
                      <Td>{r.Subjectivity}</Td>
                      <Td>{r.Confidence}</Td>
                      <Td>{r.Irony}</Td>
                    </tr>
                  ))}
                  {!filteredRows.length && (
                    <tr>
                      <Td colSpan={9} className="text-center text-gray-500 dark:text-gray-300 py-4">
                        {rows.length
                          ? "No rows for this speaker."
                          : "Upload an audio file and click “Upload & Analyze”."
                        }
                      </Td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* (Optional) Diarized Transcript viewer */}
        {!!diarization.length && (
          <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md overflow-hidden">
            <div className="px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b">
              <h3 className="text-sm font-bold">Diarized Transcript</h3>
            </div>
            <div className="p-3 space-y-2 text-sm">
              {diarization
                .filter(u => speakerFilter === "ALL" || u.speaker === speakerFilter)
                .map((u, idx) => (
                  <div key={idx} className="flex gap-2">
                    <span className="text-xs text-gray-500 w-20">{u.speaker}</span>
                    <span className="text-xs text-gray-400 w-16">{fmt(u.start)}–{fmt(u.end)}</span>
                    <span className="flex-1">{u.text}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Page-scoped loading overlay (NOT global) */}
      {busy && (
        <div
          className="absolute inset-0 z-10 bg-black/40 backdrop-blur-[1px] flex items-center justify-center rounded-xl"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex items-center gap-3 text-white bg-black/60 px-4 py-3 rounded-xl border border-white/10">
            <Spinner size={24} light />
            <span className="font-medium">Analyzing audio…</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- tiny UI helpers ---------- */
function Th({ children }) {
  return <th className="text-left px-3 py-2 font-semibold">{children}</th>;
}

function Td({ children, colSpan, className = "" }) {
  return (
    <td
      colSpan={colSpan}
      className={`px-3 py-2 align-top text-gray-900 dark:text-gray-100 ${className}`}
    >
      {children}
    </td>
  );
}

function Btn({ children, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg bg-black text-white dark:bg-uasYellow dark:text-black px-3 py-1.5 text-xs font-semibold border border-black/80 dark:border-transparent disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
      <span className="text-gray-700 dark:text-gray-200">{label}</span>
    </div>
  );
}

function Spinner({ size = 18, light = false, className = "" }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-t-transparent ${
        light ? "border-white" : "border-gray-900"
      } ${className}`}
      style={{ width: size, height: size }}
      aria-label="Loading"
      role="status"
    />
  );
}
